/**
 * Кликабельные адреса в тексте задания домашки (1.1
 * `docs/homework-scratch-design.md`). Строка режется на куски, а ссылку из
 * куска собирает React-элементом `LinkifiedText`: HTML не строится нигде, и
 * `<script>` в тексте остаётся текстом.
 *
 * ⚠️ Только для текста ПРЕПОДАВАТЕЛЯ. Ответ ученика и пометки этой функцией не
 * пропускаются: иначе ученик подсовывал бы преподавателю кликабельную ссылку.
 *
 * Правила:
 * - ссылка только со схемой `http://` или `https://` (регистр любой); `www.`
 *   без схемы, `//host`, `javascript:`, `data:`, `vbscript:` остаются текстом;
 * - адрес кончается на пробельном символе или на `<`, `>`, `"`;
 * - хвостовая пунктуация `.,;:!?`, кавычки и закрывающая скобка без пары в
 *   адрес не входят, а парная скобка внутри (`wiki/X_(Y)`) остаётся;
 * - кандидат проходит `new URL()` с протоколом http или https, без логина и
 *   пароля и без символов направления текста, иначе текст.
 *
 * Читателей у правил двое. Веб показывает ссылки (`LinkifiedText`), а сервер
 * сверяет присланный адрес отметки нажатия: после `new URL(href).href` он
 * обязан найтись в `linkHrefs(task_text)`, иначе отказ (Р7
 * `docs/homework-media-plan.md`). Поэтому файл живёт в `@edu/shared`: две
 * копии правил разъехались бы, и сервер отказывал бы видимой ученику ссылке.
 */

export type LinkPiece = { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

/** Начало адреса: схема не приклеена к букве или цифре слева. */
const CANDIDATE = /(?<![\p{L}\p{N}])https?:\/\/[^\s<>"]+/giu;

const TRAILING_PUNCT = new Set([".", ",", ";", ":", "!", "?", "'", "»", "”", "’", "“", "«"]);
const PAIRS: Record<string, string> = { ")": "(", "]": "[", "}": "{" };

/** Отрезать хвост, который адресу не принадлежит. Линейно: счётчики скобок
 * считаются одним проходом и дальше только уменьшаются при отрезании. */
function trimTail(raw: string): string {
  const counts: Record<string, number> = { "(": 0, ")": 0, "[": 0, "]": 0, "{": 0, "}": 0 };
  for (const ch of raw) if (ch in counts) counts[ch]!++;
  let end = raw.length;
  while (end > 0) {
    const last = raw[end - 1]!;
    const open = PAIRS[last];
    if (TRAILING_PUNCT.has(last)) {
      end--;
    } else if (open !== undefined && counts[last]! > counts[open]!) {
      counts[last]!--;
      end--;
    } else {
      break;
    }
  }
  return raw.slice(0, end);
}

/**
 * Управляющие символы направления текста: переворачивают видимый адрес, и
 * показанное перестаёт совпадать с тем, куда ведёт ссылка. Такой кандидат
 * остаётся текстом целиком.
 */
const BIDI = /[\u202A-\u202E\u2066-\u2069\u200E\u200F\u061C]/u;

/**
 * ⚠️ Логин и пароль в адресе запрещены: `https://good.com@evil.com/` читается
 * как good.com, а ведёт на evil.com. Обратный слеш (`https://good.com\@evil.com`)
 * парсер читает как `/`, то есть ведёт туда, что показано, и это допустимо.
 */
function safeHref(candidate: string): string | null {
  if (BIDI.test(candidate)) return null;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.hostname === "") return null;
  if (url.username !== "" || url.password !== "") return null;
  return url.href;
}

export function linkify(text: string): LinkPiece[] {
  const out: LinkPiece[] = [];
  let buf = "";
  let pos = 0;
  for (const m of text.matchAll(CANDIDATE)) {
    const start = m.index;
    const candidate = trimTail(m[0]);
    const href = candidate === "" ? null : safeHref(candidate);
    if (href === null) continue;
    buf += text.slice(pos, start);
    if (buf !== "") out.push({ kind: "text", text: buf });
    buf = "";
    out.push({ kind: "link", text: candidate, href });
    pos = start + candidate.length;
  }
  buf += text.slice(pos);
  if (buf !== "") out.push({ kind: "text", text: buf });
  return out;
}

/**
 * Адреса ссылок текста без повторов, в порядке первого появления. Ключ отметки
 * нажатия это `href`, а не порядковый номер: один адрес дважды в тексте это
 * одна ссылка (Р7 `docs/homework-media-plan.md`).
 */
export function linkHrefs(text: string): string[] {
  const seen = new Set<string>();
  for (const p of linkify(text)) if (p.kind === "link") seen.add(p.href);
  return [...seen];
}
